// React and hooks
import React, { useState, useEffect } from 'react';

// Icons
import { Search, X } from 'lucide-react';

// API client
import api from '../../lib/api';

// Components
import AddProductWeightModal from './AddProductWeightModal';

// Hooks
import useTheme from '../../hooks/useTheme';

// i18n
import { useTranslation } from 'react-i18next';

const AddEntryModal = ({ isOpen, onClose, onCreated, locationKey, selectedDate }) => {
  // State
  const [templates, setTemplates] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [avgWeight, setAvgWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [mode, setMode] = useState(null);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [search, setSearch] = useState('');

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // UI helpers
  const baseBtn =
    theme === 'dark'
      ? "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition bg-white text-black hover:bg-black hover:text-white hover:scale-105"
      : "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition bg-gray-900 text-white hover:bg-white hover:text-black hover:scale-105";

  const getId = (obj) => obj?.id || obj?._id;
  const getSupplierId = (p) => p?.supplierId || p?.supplier?._id;
  const currentInventory = selected
    ? inventory.find((item) => item.productId === getId(selected)) ||
      inventory.find((item) =>
        item.name?.trim().toLocaleLowerCase() === selected.name?.trim().toLocaleLowerCase() &&
        item.brandId === selected.brandId &&
        item.categoryId === selected.categoryId &&
        item.supplierId === getSupplierId(selected)
      )
    : null;
  const currentBoxes = Number(currentInventory?.totalBoxes || 0);
  const currentWeight = Number(currentInventory?.totalWeight || 0);
  const addedBoxes = Number(quantity) || 0;
  const addedWeight = mode === 'average'
    ? addedBoxes * (Number(avgWeight) || 0)
    : selected?.hasBaseWeight
      ? addedBoxes * Number(selected.fixedWeight || 0)
      : 0;

  // Load templates, brands, categories and suppliers when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const loadData = async () => {
      try {
        const [tmplRes, brandRes, catRes, supRes, inventoryRes] = await Promise.all([
          api.get('/product-templates'),
          api.get('/brands'),
          api.get('/categories'),
          api.get('/suppliers'),
          api.get(`/inventory/summary/products?location=${locationKey}`),
        ]);
        setTemplates(Array.isArray(tmplRes.data) ? tmplRes.data : []);
        setBrands(Array.isArray(brandRes.data) ? brandRes.data : []);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
        setSuppliers(Array.isArray(supRes.data) ? supRes.data : []);
        setInventory(Array.isArray(inventoryRes.data) ? inventoryRes.data : []);
      } catch (err) {
        console.error('Failed to load data', err);
      }
    };
    loadData();
  }, [isOpen, locationKey]);

  // Reset all form fields to default values
  const resetForm = () => {
    setSelected(null);
    setQuantity('');
    setAvgWeight('');
    setNotes('');
    setMode(null);
    setSearch('');
  };

  // Close modal and reset form
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Helper: brand name
  const brandName = (p) => {
    const brand = brands.find((b) => getId(b) === (p.brandId || p.brand?._id));
    return brand ? brand.name : '-';
  };

  // Helper: category name
  const categoryName = (p) => {
    const cat = categories.find((c) => getId(c) === (p.categoryId || p.category?._id));
    return cat ? cat.name : '-';
  };

  // Helper: supplier name
  const supplierName = (p) => {
    const sup = suppliers.find((s) => getId(s) === (p.supplierId || p.supplier?._id));
    return sup ? sup.name : p.supplier || '-';
  };

  // Helper: option label
  const renderLabel = (p) => {
    return `${p.name} — ${t('brand')}: ${brandName(p)} — ${t('category')}: ${categoryName(p)} — ${t('supplier')}: ${supplierName(p)}`;
  };

  // Save entry to backend with proper payload depending on mode
  const handleSave = async () => {
    if (!selected) return;

    try {
      const templateId = getId(selected);
      const supplierId = getSupplierId(selected);

      // For fixed-weight products, force fixed mode on focus or save
      if (selected.hasBaseWeight && mode !== 'fixed') setMode('fixed');

      // Guard rails
      if (selected.hasBaseWeight || mode === 'fixed') {
        if (!quantity || Number(quantity) <= 0) {
          alert(t('enterValidQuantity'));
          return;
        }
      } else if (!mode) {
        alert(t('selectMode') || 'Select a mode: Average or Box by box.');
        return;
      }

      // Build payload — IMPORTANT: include date
      let payload = {
        templateId,
        notes,
        supplierId,
        date: selectedDate
      };

      if (selected.hasBaseWeight || mode === 'fixed') {
        payload.quantity = Number(quantity);
      } else if (mode === 'average') {
        if (!quantity || Number(quantity) <= 0 || !avgWeight || Number(avgWeight) <= 0) {
          alert(t('enterQuantityAndAvg') || 'Enter quantity and average weight.');
          return;
        }
        payload.quantity = Number(quantity);
        payload.averageWeight = Number(avgWeight);
        payload.totalWeight = Number(quantity) * Number(avgWeight);
      } else if (mode === 'byWeight') {
        setShowWeightModal(true);
        return;
      }

      // Debug log
      console.log('➡️ Sending /inventory/entries', {
        location: locationKey,
        hasBaseWeight: selected.hasBaseWeight,
        mode,
        payload
      });

      // ✅ CORRECT ENDPOINT
      await api.post(`/inventory/entries?location=${locationKey}`, payload);

      if (onCreated) onCreated();
      handleClose();
    } catch (err) {
      console.error('❌ Error saving entry:', err?.response?.data || err?.message || err);
      alert(t('failedRegisterEntry'));
    }
  };

  // Search filter
  const term = search.toLowerCase();
  const filtered = templates.filter(
    (p) =>
      p.name?.toLowerCase().includes(term) ||
      brandName(p).toLowerCase().includes(term) ||
      categoryName(p).toLowerCase().includes(term) ||
      supplierName(p).toLowerCase().includes(term)
  );

  if (!isOpen) return null;

  // Render
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md w-full max-w-md space-y-4 relative">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-600 dark:text-gray-300 hover:text-red-600"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold text-gray-800 dark:text-white">{t('newEntry')}</h2>

        {/* Search */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
          <Search size={16} className="text-gray-500 dark:text-gray-300 mr-2" />
          <input
            type="text"
            placeholder={t('searchTemplate')}
            className="bg-transparent outline-none flex-1 text-sm text-gray-700 dark:text-gray-200"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Template list */}
        <div className="max-h-40 overflow-y-auto border rounded-md">
          {filtered.length > 0 ? (
            filtered.map((p) => (
              <button
                key={getId(p)}
                onClick={() => {
                  setSelected(p);
                  setMode(null);
                  setQuantity('');
                  setAvgWeight('');
                }}
                className={`block w-full text-left px-3 py-2 text-sm ${
                  getId(selected) === getId(p)
                    ? 'bg-emerald-600 text-white'
                    : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
                }`}
              >
                {renderLabel(p)}
              </button>
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-gray-500">{t('noTemplatesFound')}</p>
          )}
        </div>

        {/* Inputs */}
        {selected && (
          <div className="space-y-3">
            {selected.hasBaseWeight && (
              <input
                type="number"
                placeholder={t('numberOfBoxes')}
                className="w-full border px-4 py-2 rounded-md dark:bg-gray-700 dark:text-white"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onFocus={() => setMode('fixed')}
              />
            )}

            {!selected.hasBaseWeight && (
              <>
                {!mode && (
                  <div className="flex gap-2">
                    <button onClick={() => setMode('average')} className={baseBtn}>
                      {t('averageWeight')}
                    </button>
                    <button onClick={() => setMode('byWeight')} className={baseBtn}>
                      {t('boxByBox')}
                    </button>
                  </div>
                )}

                {mode === 'average' && (
                  <div className="space-y-2">
                    <input
                      type="number"
                      placeholder={t('numberOfBoxes')}
                      className="w-full border px-4 py-2 rounded-md dark:bg-gray-700 dark:text-white"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder={t('averageWeightPerBox')}
                      className="w-full border px-4 py-2 rounded-md dark:bg-gray-700 dark:text-white"
                      value={avgWeight}
                      onChange={(e) => setAvgWeight(e.target.value)}
                    />
                  </div>
                )}
              </>
            )}

            <textarea
              placeholder={t('notesOptional')}
              className="w-full border px-4 py-2 rounded-md dark:bg-gray-700 dark:text-white"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        )}

        {selected && (selected.hasBaseWeight || mode) && (
          <div className="rounded-md bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
            <p>{t('availableNow')}: {currentBoxes} {t('boxes')} · {currentWeight.toFixed(2)} KG</p>
            <p className="font-semibold">{t('projectedAfterEntry')}: {currentBoxes + addedBoxes} {t('boxes')} · {(currentWeight + addedWeight).toFixed(2)} KG</p>
          </div>
        )}

        {selected && (selected.hasBaseWeight || mode) && (
          <div className="flex justify-end gap-2">
            <button onClick={handleClose} className={baseBtn}>{t('cancel')}</button>
            <button onClick={handleSave} className={baseBtn}>{t('save')}</button>
          </div>
        )}

        {showWeightModal && selected && (
          <AddProductWeightModal
            isOpen={showWeightModal}
            onClose={() => setShowWeightModal(false)}
            product={selected}
            onUpdated={() => {
              if (onCreated) onCreated();
              handleClose();
            }}
            mode="entry"
            locationKey={locationKey}
            notes={notes}
          />
        )}
      </div>
    </div>
  );
};

export default AddEntryModal;
